import React, { useLayoutEffect, useRef, useState } from 'react';
import { FiMinus, FiPlus } from 'react-icons/fi';

const Calculator = function ({open, anchor, onClose}) {
  const [minimized, setMinimized] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [display, setDisplay] = useState('0');
  const [stored, setStored] = useState(null);
  const [operator, setOperator] = useState(null);
  const [fresh, setFresh] = useState(true);
  const [position, setPosition] = useState(null);
  const panel = useRef(null);
  const drag = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;

    if (anchor) {
      const bounds = panel.current.getBoundingClientRect();

      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - bounds.width, anchor.x)),
        y: Math.max(0, Math.min(window.innerHeight - bounds.height, anchor.y))
      });
    }

    panel.current.focus({preventScroll: true});
  }, [open, anchor]);

  const calculate = (a, b, op) => ({
    '+': () => a + b,
    '−': () => a - b,
    '×': () => a * b,
    '÷': () => a / b
  })[op]();

  const press = key => {
    if (key === 'C') {
      setDisplay('0');
      setStored(null);
      setOperator(null);
      setFresh(true);
      return;
    }
    
    if (key === '⌫') {
      if (fresh || display === 'Error') return;
      setDisplay(value => value.length > 1 && !(value.length === 2 && value.startsWith('-')) ? value.slice(0, -1) : '0');
      return;
    }

    if (key === '±' || key === '%') {
      setDisplay(value => String(key === '%' ? Number(value) / 100 : -Number(value)));
      return;
    }

    if ('0123456789.'.includes(key)) {
      setDisplay(value => fresh || value === 'Error' ? key === '.' ? '0.' : key : key === '.' && value.includes('.') ? value : value === '0' && key !== '.' ? key : value + key);
      setFresh(false);
      return;
    }

    const value = Number(display);
    const result = operator && !fresh ? calculate(stored, value, operator) : value;

    setDisplay(Number.isFinite(result) ? String(Number(result.toPrecision(12))) : 'Error');
    setStored(key === '=' ? null : result);
    setOperator(key === '=' ? null : key);
    setFresh(true);
  };

  return <section ref={panel} hidden={!open} className={`calculator ${minimized ? 'isMinimized' : ''}`} style={position ? {
    left: position.x,
    top: position.y,
    right: 'auto',
    bottom: 'auto'
  } : {}} aria-label='Calculator' tabIndex={0} onKeyDown={event => {
    event.stopPropagation();

    if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
    if (event.key.toLowerCase() === 'c') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === 'Enter' && event.target.closest('button')) return;

    const key = {
      c: 'C',
      Enter: '=',
      Escape: 'C',
      Backspace: '⌫',
      '*': '×',
      '/': '÷',
      '-': '−'
    }[event.key] || event.key;

    if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '+', '−', '×', '÷', '=', '%', 'C', '⌫'].includes(key)) {
      event.preventDefault();
      press(key);
    }
  }}>
    <div className={`calculatorHeader${dragging ? ' isDragging' : ''}`} onPointerDown={event => {
      if (event.target.closest('button') || event.button !== 0) return;
      const rect = panel.current.getBoundingClientRect();
      drag.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
      setDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }} onPointerMove={event => {
      if (!drag.current) return;
      const rect = panel.current.getBoundingClientRect();
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - rect.width, event.clientX - drag.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - rect.height, event.clientY - drag.current.y))
      });
    }} onPointerUp={() => {
      drag.current = null;
      setDragging(false);
    }} onPointerCancel={() => {
      drag.current = null;
      setDragging(false);
    }} onLostPointerCapture={() => {
      drag.current = null;
      setDragging(false);
    }}>
      <strong>calculator</strong>
      <button
        className='minimizeNoteButton'
        aria-label={minimized ? 'restore calculator' : 'minimize calculator'}
        aria-expanded={!minimized}
        title={minimized ? 'restore calculator' : 'minimize calculator'}
        onClick={() => setMinimized(value => !value)}
      >
        {minimized ? <FiPlus size={12} aria-hidden='true' /> : <FiMinus size={12} aria-hidden='true' />}
      </button>
      <button
        className='deleteNoteButton'
        aria-label='close calculator'
        title='close calculator'
        onClick={onClose}
      >
        <span aria-hidden='true' />
      </button>
    </div>
    <div className='calculatorBody' hidden={minimized}>
      <output aria-live='polite'>{display}</output>
      <div className='calculatorKeys'>{['C', '±', '%', '÷', '7', '8', '9', '×', '4', '5', '6', '−', '1', '2', '3', '+', '⌫', '0', '.', '='].map(key => <button
        key={key}
        aria-pressed={['+', '−', '×', '÷'].includes(key) ? operator === key : undefined}
        onClick={() => press(key)}
      >{key}</button>)}</div>
    </div>
  </section>;
};

export default Calculator;
